// frontend/src/hooks/useOnePageScroll.js
import { useEffect, useRef } from "react";

export default function useOnePageScroll({
  selector = ".home-section",
  lockMs = 750,
  threshold = 18,
  enabled = true,

  // ✅ 추가: 내부 스크롤 컨테이너 지정 (둘 중 하나만 써도 됨)
  containerSelector = ".page-content",
  containerRef = null,
} = {}) {
  const lockRef = useRef(false);
  const touchStartYRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    const container =
      (containerRef && containerRef.current) ||
      (containerSelector ? document.querySelector(containerSelector) : null) ||
      null;

    if (!container) return;

    const sections = Array.from(container.querySelectorAll(selector));
    if (sections.length === 0) return;

    const lock = () => {
      lockRef.current = true;
      window.setTimeout(() => (lockRef.current = false), lockMs);
    };

    const getCurrentIndex = () => {
      const y = container.scrollTop;
      let bestIdx = 0;
      let bestDist = Infinity;

      sections.forEach((el, idx) => {
        // el.offsetTop은 컨테이너 기준으로 누적된 top (컨테이너가 position:relative여도 OK)
        const top = el.offsetTop;
        const dist = Math.abs(top - y);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = idx;
        }
      });
      return bestIdx;
    };

    const goToIndex = (idx) => {
      const target = sections[idx];
      if (!target) return;
      container.scrollTo({ top: target.offsetTop, behavior: "smooth" });
    };

    const onWheel = (e) => {
      if (lockRef.current) return;
      if (Math.abs(e.deltaY) < threshold) return;

      e.preventDefault();

      const idx = getCurrentIndex();
      const dir = e.deltaY > 0 ? 1 : -1;
      const next = Math.max(0, Math.min(sections.length - 1, idx + dir));

      if (next !== idx) {
        lock();
        goToIndex(next);
      }
    };

    // 모바일 스와이프(터치)
    const onTouchStart = (e) => {
      touchStartYRef.current = e.touches?.[0]?.clientY ?? null;
    };

    const onTouchMove = (e) => {
      if (lockRef.current) return;

      const startY = touchStartYRef.current;
      const nowY = e.touches?.[0]?.clientY ?? null;
      if (startY == null || nowY == null) return;

      const delta = startY - nowY; // 아래로 스와이프하면 +
      if (Math.abs(delta) < 40) return;

      e.preventDefault();

      const idx = getCurrentIndex();
      const dir = delta > 0 ? 1 : -1;
      const next = Math.max(0, Math.min(sections.length - 1, idx + dir));

      if (next !== idx) {
        lock();
        goToIndex(next);
      }

      touchStartYRef.current = null;
    };

    // ✅ container에 등록 + passive:false 필수
    container.addEventListener("wheel", onWheel, { passive: false });
    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      container.removeEventListener("wheel", onWheel);
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
    };
  }, [enabled, selector, lockMs, threshold, containerSelector, containerRef]);
}